/**
 * Every word the site says, in the language it was written in.
 *
 * English is the source catalogue: `Messages` is inferred from this object, so
 * the other two are checked against it and a key added here without a Dutch or
 * Thai counterpart is a type error rather than a page that silently falls back
 * to English. Nothing here is a template string built by concatenation, because
 * a sentence assembled from fragments is a sentence that only works in the
 * language it was assembled for; anything with a number in it takes the number
 * as an argument and returns the finished sentence.
 */
export const en = {
  nav: {
    home: "SprinkleDays home",
    guides: "Guides",
    questions: "Questions",
    allGuides: "All guides",
    startCourse: "Start a course",
    openToday: "Open today’s sheet",
    howItWorks: "How it works",
    reading: "Reading",
    footer: "Footer",
    breadcrumb: "Breadcrumb",
    language: "Language",
    languageOf: (label: string) => `Read this page in ${label}`,
    footerBlurb:
      "SprinkleDays turns a curriculum into one printable sheet a day: two languages side by side, split by a fold line. No account, and nothing leaves your device.",
  },

  languages: {
    en: "English",
    nl: "Dutch",
    th: "Thai",
    /** "Thai, English or Dutch", for the headline a screen reader is given. */
    or: (names: readonly string[]) =>
      `${names.slice(0, -1).join(", ")} or ${names.at(-1)}`,
    to: (from: string, to: string) => `${from} to ${to}`,
  },

  home: {
    headline: {
      lead: "Learn ",
      emphasis: "a few words",
      tail: " a day.",
    },
    lede: "One sheet a day, two languages side by side. Print the whole week for the kitchen table, or read the day’s sheet on your phone. Learning alongside someone else? Fold the sheet down the middle and test each other.",
    learning: "You are learning",
    speak: "You already speak",
    wordsADay: "Words a day",
    sentences: "Sentences",
    whoIsLearning: "Who is learning",
    justMe: "Just me",
    twoOfUs: "Two of us",
    nameLeft: "Name on the left",
    nameRight: "Name on the right",
    namePlaceholderLeft: "e.g. Papa",
    namePlaceholderRight: "e.g. Nok",
    namesHint:
      "Both names print above the columns, so each of you knows which half to fold away.",
    start: "Start with day 1",
    save: "Save and open today",
    fine: "No account, no card, nothing to cancel. Everything stays on this device.",
    sampleTag: "One A4 sheet a day",
    sampleTitle: "This is what a day looks like",
    sampleLede:
      "Your two languages, side by side, with a fold line down the middle. The sheet updates as you change the settings above.",
    sampleNotes: [
      "Fold along the line to hide one side and check yourself",
      "Print the whole week in one go, or one day at a time",
      "On screen, tap any word to hear how it sounds",
      "Scan the printed sheet to open the day and tick it off",
    ],
    howTitle: "Three minutes at the kitchen table",
    steps: [
      {
        title: "Pick your two languages",
        body: "The one you are learning and the one you already speak. Thai, Dutch, and English, in any pair.",
      },
      {
        title: "Print the week",
        body: "Seven A4 sheets, one for each day, both languages side by side. Or keep them on screen and tap any word to hear it.",
      },
      {
        title: "Fold and test",
        body: "Fold along the dotted line so one side hides, and check yourself. Learning with someone else? Take turns.",
      },
    ],
    premiumTag: "Premium",
    premiumJoinedTitle: "You are on the list.",
    premiumJoinedLede:
      "Every week of the course is yours on this device. We write rarely, and only about the course.",
    premiumTitleStrong: "Go premium",
    premiumTitleRest: " and open every week",
    premiumLede:
      "One course, whether one of you learns from it or five. A whole week of sheets opens at once, ready to print in one go or read on your phone.",
    premiumAsk:
      "Leave your email to join the early list, and every week of the course opens on this device.",
    premiumSubmit: "Go premium",
    premiumFine:
      "We send your email, your two languages, and the pace you set. Your sheets, your names, and your progress stay on this device.",
    readsTag: "Before you start",
    readsTitle: "Worth five minutes of reading",
    readsBody:
      "How many words a day to set, why folding the sheet beats rereading it, and what a first month of Thai should actually cover.",
    readsInEnglish: "The guides are written in English.",
    footerBlurb:
      "Learn a little every day. Made for anyone picking up a language a few words at a time, on their own or with the people they live with.",
  },

  articles: {
    tag: "Guides",
    title: "Learning a language a few words at a time",
    minRead: (minutes: number) => `${minutes} min read`,
    ctaTitle: "Start with day 1",
    ctaBody:
      "Pick two languages and how many words a day suits you. The first sheet is ready before you finish reading this sentence, and there is no account to make. Questions first?",
    ctaLink: "Read the answers",
    categories: {} as Record<string, string>,
  },

  faq: {
    tag: "Questions",
    title: "Questions about SprinkleDays",
    moreTitle: "Longer answers",
    moreBodyBefore: "The questions above are about SprinkleDays. The",
    moreBodyLink: "guides",
    moreBodyAfter: "answer the harder ones about learning a language at all.",
    ctaTitle: "Nothing left to decide",
    ctaBody:
      "Two languages, a word count, and day 1. Everything stays on this device, and the first sheet is ready in about a minute.",
    ctaButton: "Start with day 1",
  },

  notFound: {
    title: "This page wandered off.",
    body: "The address is not one we have. Everything else is where it was.",
    cta: "Go to the start",
    asideBefore: "Landed here from a search? The",
    asideGuides: "guides",
    asideAnd: "and the",
    asideQuestions: "questions page",
    asideAfter: "are still there.",
  },

  app: {
    mainNav: "Main",
    today: "Today",
    thisWeek: "This week",
    settings: "Settings",
    booting: "Opening your sheets…",
    loading: "Loading…",
    gettingWeekReady: "Getting the week ready…",
    retry: "Try again",
    day: (day: number) => `Day ${day}`,
    dayOf: (day: number, total: number) => `${day} / ${total}`,
    previousDay: "Previous day",
    nextDay: "Next day",
    backToWeek: "Back to this week",
    notReleasedTitle: "Not yet. That is the point.",
    notReleasedBody: (day: number, week: number, date: string) =>
      `Sheets arrive a week at a time. Day ${day} comes with week ${week}, on ${date}.`,
    finished:
      "You have reached the end of the course. Every sheet stays open for another round.",
    weekTag: (words: number, days: number) =>
      `${words} words a day · ${days} days`,
    week: (week: number) => `Week ${week}`,
    weekOf: (week: number, total: number) => `${week} / ${total}`,
    daysDone: (done: number, total: number) => `${done} of ${total} days done`,
    earlierWeek: "Earlier week",
    laterWeek: "Later week",
    dayLocked: (day: number, weekday: string) =>
      `Day ${day}, ${weekday}, locked`,
    dayLabel: (day: number, weekday: string, done: boolean) =>
      `Day ${day}, ${weekday}${done ? ", done" : ""}`,
    arriveOn: (date: string) => `These sheets arrive on ${date}.`,
    weekWaitingTitle: (week: number) => `Week ${week} is waiting.`,
    weekWaitingBody: (day: number) =>
      `Both languages side by side, a whole week to print in one go. Open day ${day} to keep going.`,
    openDay: (day: number) => `Open day ${day}`,
    printWeek: "Print this week",
    sheetsNote: (sheets: number) =>
      `${sheets} ${sheets === 1 ? "sheet" : "sheets"}, one A4 page each. Fold each sheet down the middle and tick the day off when it is done.`,
    sheetsPending: "Getting the sheets ready…",

    settingsTitle: "Change anything, keep your progress.",
    settingsLede: "Everything saves as you go, and stays on this device.",
    languagesPanel: "Languages",
    leftColumn: "Left column",
    rightColumn: "Right column",
    nameAboveLeft: "Name above the left column",
    nameAboveRight: "Name above the right column",
    namePlaceholderLeft: "Optional, e.g. Papa",
    namePlaceholderRight: "Optional, e.g. Nok",
    showPronunciation: "Show how to say the Thai",
    pacePanel: "How much a day",
    sentencesADay: "Sentences a day",
    paceNote:
      "More words a day means fewer days to the end of the course. The words themselves stay in the same order.",
    coursePanel: "Your course",
    started: "Started",
    timezone: "Time zone",
    access: "Access",
    fullCourse: "Full course",
    restartPanel: "Start again at day 1",
    restartBody:
      "Starting again keeps your languages and how much you do each day, and clears every day you marked done.",
    restartConfirm: "Clear all your progress?",
    restartYes: "Yes, start again",
    restartNo: "Keep it",
    restart: "Start again",

    swapSides: "Swap the two sides",
    leftSide: "Left side of the sheet",
    rightSide: "Right side of the sheet",
    decrease: (unit: string) => `One ${unit} fewer`,
    increase: (unit: string) => `One ${unit} more`,
    unitWord: "word",
    unitSentence: "sentence",

    sheetFailed: "The sheet did not load.",
    printingSheet: "Printing today’s sheet…",
    markDone: "Mark day done",
    notDoneYet: "Not done yet",
    print: "Print",
    pacePill: (words: number, sentences: number) =>
      `${words} words · ${sentences} ${sentences === 1 ? "sentence" : "sentences"}`,
    controlsNote:
      "Changing the words a day changes the pace, not the content: the same list is simply spread over more or fewer days.",

    email: "Your email",
    emailPlaceholder: "you@example.com",
    emailInvalid: "That does not look like an email address.",
    sendFailed: "Something went wrong. Please try again.",
    sending: "One moment…",
  },

  seo: {
    homeTitle: "SprinkleDays | Learn a language a few words a day",
    homeDescription:
      "Learn a language a few words a day, at a pace you set. One printable sheet with two languages side by side, for learning on your own or testing each other.",
    articlesTitle: "Guides to learning a language a few words a day | SprinkleDays",
    articlesDescription:
      "Practical guides to daily language practice: choosing a pace you can keep, testing yourself on paper, and starting Thai from scratch.",
    faqTitle: "Questions about SprinkleDays | SprinkleDays",
    faqDescription:
      "What SprinkleDays costs, which languages it covers, how the folded sheets work, and what does and does not leave your device.",
    homeLabel: "SprinkleDays: learn a language a few words a day",
    articlesLabel: "All guides to daily language practice",
    faqLabel: "Questions about SprinkleDays",
  },
};

/**
 * Inferred rather than declared, and deliberately not `as const`: literal types
 * here would demand that Dutch use the English words.
 */
export type Messages = typeof en;
